import type { KioskWidgetType } from '@cluster/types';
import { DISCOUNT_STATES, type DiscountState } from './discount';

/**
 * Named display-data slots a host app must feed at runtime for the widget to
 * render its server-returned content. Slots are opaque identifiers — the
 * renderer never fetches or computes; T05/T06 bind each slot to the
 * corresponding server string projection (quote display strings, eligible
 * record lists, tender menus, scan inputs). Slot names are renderer-owned
 * display interpretation per the design doc §"Renderer contract".
 */
export const DATA_SLOT_NAMES = Object.freeze([
  'categories',
  'products',
  'featuredProduct',
  'quote',
  'promotions',
  'tenders',
  'identification',
  'scan',
] as const);

export type DataSlotName = (typeof DATA_SLOT_NAMES)[number];

/**
 * Per-widget render contract. Human-facing widget metadata (labels, icons,
 * min sizes, config field descriptors) is served by `GET /v1/kiosks/widgets`
 * at runtime and is deliberately NOT duplicated here — the registry's job is
 * identifier membership plus binding/state interpretation, which keeps this
 * package drift-free against the service catalog.
 */
export interface WidgetRenderContract {
  readonly type: KioskWidgetType;
  /** Ordered slots the host binds server-returned display data into. */
  readonly dataSlots: readonly DataSlotName[];
  /** Frozen display state machine names this widget renders variants for. */
  readonly stateVariants: readonly DiscountState[];
}

const discountVariants: readonly DiscountState[] = Object.freeze([...DISCOUNT_STATES]);
const noVariants: readonly DiscountState[] = Object.freeze([]);

/**
 * Widget identifier → render contract. The `as const satisfies` check is the
 * compile-time drift gate: if `KioskWidgetType` in `@cluster/types` gains,
 * loses, or renames an identifier, `tsc` fails this file (missing key, excess
 * key, or literal-type mismatch) until the registry is updated in lockstep.
 */
export const WIDGET_RENDER_REGISTRY = {
  categories: {
    type: 'categories',
    dataSlots: ['categories'],
    stateVariants: noVariants,
  },
  'product-list': {
    type: 'product-list',
    dataSlots: ['products'],
    stateVariants: noVariants,
  },
  'featured-product': {
    type: 'featured-product',
    dataSlots: ['featuredProduct'],
    stateVariants: discountVariants,
  },
  banner: {
    type: 'banner',
    dataSlots: [],
    stateVariants: noVariants,
  },
  button: {
    type: 'button',
    dataSlots: [],
    stateVariants: noVariants,
  },
  image: {
    type: 'image',
    dataSlots: [],
    stateVariants: noVariants,
  },
  text: {
    type: 'text',
    dataSlots: [],
    stateVariants: noVariants,
  },
  cart: {
    type: 'cart',
    dataSlots: ['quote'],
    stateVariants: discountVariants,
  },
  search: {
    type: 'search',
    dataSlots: [],
    stateVariants: noVariants,
  },
  filters: {
    type: 'filters',
    dataSlots: [],
    stateVariants: noVariants,
  },
  promotions: {
    type: 'promotions',
    dataSlots: ['promotions'],
    stateVariants: discountVariants,
  },
  payment: {
    type: 'payment',
    dataSlots: ['tenders'],
    stateVariants: noVariants,
  },
  'login-identification': {
    type: 'login-identification',
    dataSlots: ['identification'],
    stateVariants: noVariants,
  },
  'card-qr-scanner': {
    type: 'card-qr-scanner',
    dataSlots: ['scan'],
    stateVariants: noVariants,
  },
  'voice-agent': {
    type: 'voice-agent',
    dataSlots: [],
    stateVariants: noVariants,
  },
} as const satisfies Record<KioskWidgetType, WidgetRenderContract>;

const REGISTRY_KEYS = Object.freeze(Object.keys(WIDGET_RENDER_REGISTRY) as KioskWidgetType[]);
const KNOWN_TYPES = Object.freeze(new Set<string>(REGISTRY_KEYS));

/** All widget identifiers the renderer knows, in registry definition order. */
export function knownWidgetTypes(): readonly KioskWidgetType[] {
  return REGISTRY_KEYS;
}

/** Narrowing guard safe against prototype-inherited keys (backed by a Set). */
export function isKnownWidgetType(type: string): type is KioskWidgetType {
  return KNOWN_TYPES.has(type);
}

/** Registry lookup for a known identifier. Returns `undefined` for anything else. */
export function lookupWidgetRenderContract(type: string): WidgetRenderContract | undefined {
  if (!isKnownWidgetType(type)) return undefined;
  return WIDGET_RENDER_REGISTRY[type];
}

/** Registry lookup that fails loudly for unknown identifiers (never a silent skip). */
export function requireWidgetRenderContract(type: string): WidgetRenderContract {
  const contract = lookupWidgetRenderContract(type);
  if (!contract) {
    throw new TypeError(`Unknown kiosk widget type: ${type}`);
  }
  return contract;
}
